import { lazy, Suspense } from "react";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider } from "@/contexts/AuthContext";
import { CartProvider } from "@/contexts/CartContext";
import { FeatureFlagsProvider } from "@/contexts/FeatureFlagsContext";
import { AppSettingsProvider } from "@/contexts/AppSettingsContext";
import { NavigationProvider } from "@/contexts/NavigationContext";
import { DebugProvider } from "@/contexts/DebugContext";
import { BreadcrumbProvider } from "@/contexts/BreadcrumbContext";
import { FavoritesProvider } from "@/contexts/FavoritesContext";
import { HidePricesProvider } from "@/contexts/HidePricesContext";
import { ProtectedRoute } from "./components/auth/ProtectedRoute";
import { FeatureFlagGuard } from "./components/FeatureFlagGuard";
import { FavoriteListPicker } from "./components/favorites/FavoriteListPicker";
import { CapabilitiesProvider } from "./contexts/CapabilitiesContext";


// Eager-load the landing page for fastest initial render
import Index from "./pages/Index";

// Auto-retry dynamic imports with a page reload on chunk load failure
function lazyWithRetry(importFn: () => Promise<any>) {
  return lazy(() =>
    importFn().catch(() => {
      // Chunk likely outdated after a deploy — reload once
      const key = 'chunk_reload';
      const hasReloaded = sessionStorage.getItem(key);
      if (!hasReloaded) {
        sessionStorage.setItem(key, '1');
        window.location.reload();
        return new Promise(() => {}); // never resolves; page is reloading
      }
      sessionStorage.removeItem(key);
      return importFn(); // second attempt; will throw if still broken
    })
  );
}

// Lazy-load all other pages to reduce initial bundle size
const Orders = lazyWithRetry(() => import("./pages/Orders"));
const Units = lazyWithRetry(() => import("./pages/Units"));
const UnitDetails = lazyWithRetry(() => import("./pages/UnitDetails"));
const UnitParts = lazyWithRetry(() => import("./pages/UnitParts"));
const UnitOptions = lazyWithRetry(() => import("./pages/UnitOptions"));
const Parts = lazyWithRetry(() => import("./pages/Parts"));
const ProductsView = lazyWithRetry(() => import("./pages/ProductsView"));
const ContactInfo = lazyWithRetry(() => import("./pages/ContactInfo"));


const MessageBoard = lazyWithRetry(() => import("./pages/MessageBoard"));
const CaseManagement = lazyWithRetry(() => import("./pages/CaseManagement"));
const MySpiffs = lazyWithRetry(() => import("./pages/MySpiffs"));
const MySpiffDetail = lazyWithRetry(() => import("./pages/MySpiffDetail"));
const SalesPersonSpiffs = lazyWithRetry(() => import("./pages/SalesPersonSpiffs"));
const DealerSpiffs = lazyWithRetry(() => import("./pages/DealerSpiffs"));
const SpiffPrintView = lazyWithRetry(() => import("./pages/SpiffPrintView"));
const DealerClaimedSpiffs = lazyWithRetry(() => import("./pages/DealerClaimedSpiffs"));
const ViewDealerInvoices = lazyWithRetry(() => import("./pages/ViewDealerInvoices"));
const WarrantyRegistration = lazyWithRetry(() => import("./pages/WarrantyRegistration"));
const UnitRegistration = lazyWithRetry(() => import("./pages/UnitRegistration"));
const UnitRegistrationConfirmation = lazyWithRetry(() => import("./pages/UnitRegistrationConfirmation"));
const UnitRegistrationError = lazyWithRetry(() => import("./pages/UnitRegistrationError"));
const ServiceCampaigns = lazyWithRetry(() => import("./pages/ServiceCampaigns"));
const ServiceCampaignDetail = lazyWithRetry(() => import("./pages/ServiceCampaignDetail"));
const RGAList = lazyWithRetry(() => import("./pages/RGAList"));
const CreateRGA = lazyWithRetry(() => import("./pages/CreateRGA"));
const DealerPreAuthorizations = lazyWithRetry(() => import("./pages/DealerPreAuthorizations"));
const NewPreAuthorization = lazyWithRetry(() => import("./pages/NewPreAuthorization"));
const DealerClaims = lazyWithRetry(() => import("./pages/DealerClaims"));
const ClaimDetail = lazyWithRetry(() => import("./pages/ClaimDetail"));
const NewClaim = lazyWithRetry(() => import("./pages/NewClaim"));
const YourTickets = lazyWithRetry(() => import("./pages/YourTickets"));
const NewCase = lazyWithRetry(() => import("./pages/NewCase"));
const QuickOrder = lazyWithRetry(() => import("./pages/QuickOrder"));
const InventoryTransfers = lazyWithRetry(() => import("./pages/InventoryTransfers"));
const UnknownPartRequest = lazyWithRetry(() => import("./pages/UnknownPartRequest"));
const ProductGroups = lazyWithRetry(() => import("./pages/ProductGroups"));
const SavedCarts = lazyWithRetry(() => import("./pages/SavedCarts"));
const Cart = lazyWithRetry(() => import("./pages/Cart"));
const Checkout = lazyWithRetry(() => import("./pages/Checkout"));
const DigitalAssets = lazyWithRetry(() => import("./pages/DigitalAssets"));
const DigitalAssetDetail = lazyWithRetry(() => import("./pages/DigitalAssetDetail"));
const ProductDetail = lazyWithRetry(() => import("./pages/ProductDetail"));
const SearchResults = lazyWithRetry(() => import("./pages/SearchResults"));

const Admin = lazyWithRetry(() => import("./pages/Admin"));
const LaborOperationsLibrary = lazyWithRetry(() => import("./pages/LaborOperationsLibrary"));
const DealerUsers = lazyWithRetry(() => import("./pages/DealerUsers"));
const DealershipInfo = lazyWithRetry(() => import("./pages/DealershipInfo"));
const MyFavorites = lazyWithRetry(() => import("./pages/MyFavorites"));
const FavoriteListDetail = lazyWithRetry(() => import("./pages/FavoriteListDetail"));
const NotFound = lazyWithRetry(() => import("./pages/NotFound"));

const queryClient = new QueryClient();

const App = () => (
  <QueryClientProvider client={queryClient}>
    <AuthProvider>
      <CartProvider>
        <AppSettingsProvider>
        <FeatureFlagsProvider>
          <DebugProvider>
            <NavigationProvider>
            <TooltipProvider>
            <Sonner />
            <BrowserRouter>
              <BreadcrumbProvider>
              <HidePricesProvider>
              <FavoritesProvider>
              <ProtectedRoute>
              <CapabilitiesProvider>
          <Suspense fallback={null}>
          <Routes>
            <Route path="/" element={<Index />} />
            <Route path="/orders" element={<Orders />} />
            <Route path="/units" element={<Units />} />
            <Route path="/units/:vin" element={<UnitDetails />} />
            <Route path="/vins" element={<Navigate to="/units" />} />
            <Route path="/vins/:vin" element={<Navigate to="/units" />} />
            <Route path="/unit-parts" element={<UnitParts />} />
            <Route path="/unit-options" element={<UnitOptions />} />
            <Route path="/vin-parts" element={<Navigate to="/unit-parts" />} />
            <Route path="/vin-options" element={<Navigate to="/unit-options" />} />
            <Route path="/parts" element={<Parts />} />
            <Route path="/parts/browse/*" element={<Parts />} />
            <Route path="/parts/products/*" element={<ProductsView />} />
            <Route path="/products/:productId" element={<ProductDetail />} />
            <Route path="/search-results" element={<SearchResults />} />
            <Route path="/contact-info" element={<ContactInfo />} />
            
            
            <Route path="/message-board" element={<MessageBoard />} />
            <Route path="/case-management" element={<CaseManagement />} />
            <Route path="/my-spiffs" element={<MySpiffs />} />
            <Route path="/my-spiffs/:id" element={<MySpiffDetail />} />
            <Route path="/my-spiffs/:id/print" element={<SpiffPrintView />} />
            <Route path="/claimed-dealer-spiffs/:id/print" element={<SpiffPrintView />} />
            <Route path="/claimed-dealer-spiffs" element={<DealerClaimedSpiffs />} />
            <Route path="/salesperson-spiffs" element={<SalesPersonSpiffs />} />
            <Route path="/dealer-spiffs" element={<DealerSpiffs />} />
            <Route path="/dealer-invoices" element={<ViewDealerInvoices />} />
            <Route path="/warranty-registration" element={<WarrantyRegistration />} />
            <Route path="/unit-registration" element={<UnitRegistration />} />
            <Route path="/unit-registration/confirmation" element={<UnitRegistrationConfirmation />} />
            <Route path="/unit-registration/error" element={<UnitRegistrationError />} />
            <Route path="/service-campaigns" element={<ServiceCampaigns />} />
            <Route path="/service-campaigns/:campaignId" element={<ServiceCampaignDetail />} />
            <Route path="/rga-list" element={<RGAList />} />
            <Route path="/create-rga" element={<CreateRGA />} />
            <Route path="/dealer-pre-authorizations" element={<DealerPreAuthorizations />} />
            <Route path="/new-pre-authorization" element={<NewPreAuthorization />} />
            <Route path="/dealer-claims" element={<DealerClaims />} />
            <Route path="/dealer-claims/:claimId" element={<ClaimDetail />} />
            <Route path="/new-claim" element={<NewClaim />} />
            <Route path="/your-tickets" element={<YourTickets />} />
            <Route path="/your-tickets/new" element={<NewCase />} />
            <Route path="/quick-order" element={<QuickOrder />} />
            <Route path="/inventory-transfers" element={<InventoryTransfers />} />
            <Route path="/unknown-part-request" element={<UnknownPartRequest />} />
            <Route path="/product-groups" element={<ProductGroups />} />
          <Route path="/saved-carts" element={<SavedCarts />} />
          <Route path="/cart" element={<Cart />} />
          <Route path="/checkout" element={<Checkout />} />
            <Route path="/digital-assets" element={<DigitalAssets />} />
            <Route path="/digital-assets/:assetId" element={<DigitalAssetDetail />} />
            <Route path="/admin" element={<Admin />} />
            <Route path="/labor-operations" element={<LaborOperationsLibrary />} />
            <Route path="/dealer-users" element={<DealerUsers />} />
            <Route path="/dealership-info" element={<DealershipInfo />} />
            <Route path="/my-favorites" element={<MyFavorites />} />
            <Route path="/my-favorites/:listId" element={<FavoriteListDetail />} />
            {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
            <Route path="*" element={<NotFound />} />
          </Routes>
          </Suspense>
          <FavoriteListPicker />
              </CapabilitiesProvider>
        </ProtectedRoute>
              </FavoritesProvider>
              </HidePricesProvider>
              </BreadcrumbProvider>
      </BrowserRouter>
      </TooltipProvider>
            </NavigationProvider>
          </DebugProvider>
          </FeatureFlagsProvider>
          </AppSettingsProvider>
  </CartProvider>
</AuthProvider>
</QueryClientProvider>
);

export default App;
